import { User } from '@prisma/client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from './store/index';

const AuthContext = createContext({
  token: null as string | null,
  setToken: (token: string | null) => undefined,
});

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // State to hold the authentication token
  const [token, setToken_] = useState(localStorage.getItem('accessToken'));
  const setUser = useStore((state) => state.setUser);
  const logout = useStore((state) => state.logout);

  // Function to set the authentication token
  const setToken = (newToken: string | null): undefined => {
    if (!newToken) {
      // If token is being cleared, call logout to handle everything
      logout();
    } else {
      setToken_(newToken);
    }
  };

  useEffect(() => {
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // Clear user from store when token is removed
      setUser(null);
    }
  }, [token, setUser]);

  // Memoized value of the authentication context
  const contextValue = useMemo(
    () => ({
      token,
      setToken,
    }),
    [token]
  );

  // Provide the authentication context to the children components
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);
  const logout = useStore((state) => state.logout);

  return {
    ...context,
    user,
    setUser,
    logout,
  };
};

export default AuthProvider;
